/**
 * Manual step input parsing — pure, fully tested.
 *
 * Users write their own plan steps (typed or dictated). A trailing duration
 * like "… 15 min", "… 15m", "… (20 minutes)" becomes the step's time
 * estimate so manual steps feed the plan totals just like AI steps do.
 * Without one, a modest default applies.
 */

export interface ParsedStep {
  text: string;
  minutes: number;
}

export const DEFAULT_STEP_MINUTES = 10;

function clampMinutes(n: number): number {
  return Math.max(1, Math.min(240, Math.round(n)));
}

// ---------------------------------------------------------------------------
// Step promotion — a step that outgrew its parent becomes its own task.
// Pure: returns the parent's remaining steps and the seed for the new task;
// the store materializes ids/timestamps.
// ---------------------------------------------------------------------------

import type { ActionStep, ClassifiedTask, Task } from '../types';

export interface StepPromotion {
  /** Parent's steps with the promoted one removed. */
  parentSteps: ActionStep[];
  /** Seed for the new task — same zone and scores as the parent (the user
   *  can re-zone with ⇄; re-classifying here would surprise). */
  childSeed: ClassifiedTask;
}

/** Null when the step doesn't exist or is already completed (a done step is
 *  a record, not a workload — nothing to promote). */
export function promoteStepFrom(task: Task, stepId: string): StepPromotion | null {
  const step = task.steps.find((st) => st.id === stepId);
  if (!step || step.done) return null;
  return {
    parentSteps: task.steps.filter((st) => st.id !== stepId),
    childSeed: {
      text: step.text,
      zone: task.zone,
      urgency: task.urgency,
      importance: task.importance,
    },
  };
}

/** Returns null for empty/whitespace input. */
export function parseStepInput(raw: string): ParsedStep | null {
  const trimmed = raw.replace(/\s+/g, ' ').trim();
  if (!trimmed) return null;

  // Trailing duration: "15 min", "15min", "15 minutes", "15m", "(15 min)".
  const match = /^(.*?)[\s,:–—-]*\(?(\d{1,3})\s*(?:min(?:ute)?s?|m)\b\.?\)?$/i.exec(trimmed);
  if (match && match[1].trim().length >= 2) {
    const text = match[1].trim().replace(/[,:–—-]+$/, '').trim();
    return { text, minutes: clampMinutes(Number(match[2])) };
  }
  return { text: trimmed, minutes: DEFAULT_STEP_MINUTES };
}
